import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Logger } from 'shared';
import { I18nService } from 'i18n';

function primarySegmentAfterLang(url: string): string {
  const path = url.split('?')[0];
  const parts = path.split('/').filter(Boolean);
  const langIdx = parts.findIndex((p) => p === 'en' || p === 'de');
  if (langIdx < 0) {
    return '';
  }
  return parts[langIdx + 1] || '';
}

function langSegmentFromUrl(url: string): string {
  const path = url.split('?')[0];
  const parts = path.split('/').filter(Boolean);
  const langIdx = parts.findIndex((p) => p === 'en' || p === 'de');
  if (langIdx < 0) {
    return '';
  }
  return parts[langIdx] || '';
}

export const emailVerifiedGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const i18nService = inject(I18nService);
  const logger = new Logger('emailVerifiedGuard');

  const allowlist = (route.data?.['emailUnverifiedAllowlist'] as string[] | undefined)?.filter(Boolean) ?? [];

  return authService.getUser$().pipe(
    take(1),
    map(user => {
      if (!user) {
        logger.debug(`NO USER => REDIRECT TO /login`);
        return router.createUrlTree(['/login']);
      }

      // Wolfpack (and any app that sets allowRoutesWhenEmailUnverified): only explicit true counts as verified.
      // Other apps: legacy API may omit email_confirmed — treat like verified unless explicitly false.
      const emailVerifiedOk =
        allowlist.length > 0 ? user.email_confirmed === true : user.email_confirmed !== false;
      // Opt-in by the backend: an account whose confirmation is deferred (created
      // by a purchase) is not blocked — the app asks it to confirm instead. Only an
      // explicit `true` counts, so every other app/backend behaves as before.
      if (user.email_verification_deferred === true) {
        logger.debug(`EMAIL VERIFICATION DEFERRED => CAN ACTIVATE: ${state.url}`);
        return true;
      }

      if (emailVerifiedOk) {
        logger.debug(`EMAIL CONFIRMED => CAN ACTIVATE: ${state.url}`);
        return true;
      }

      if (allowlist.length === 0) {
        logger.debug('EMAIL NOT CONFIRMED => REDIRECT TO /profile/complete');
        return router.createUrlTree([`/${i18nService.getLanguage()}/profile/complete`]);
      }
      const primary = primarySegmentAfterLang(state.url);
      const allowed = allowlist.includes(primary) || (primary === '' && allowlist.includes('dashboard'));
      if (allowed) {
        logger.debug(`EMAIL NOT CONFIRMED => ALLOWLIST HIT: ${primary || '(root)'} for ${state.url}`);
        return true;
      }
      const lang = langSegmentFromUrl(state.url) || i18nService.getLanguage();
      logger.debug(`EMAIL NOT CONFIRMED => REDIRECT TO DASHBOARD (${lang}) from ${state.url}`);
      return router.createUrlTree([`/${lang}/dashboard`]);
    })
  );
};
